import React from "react";
import { IntakeWithEditedFlags } from "../../data/plan";
import { useFood, usePurchase, usePurchasesByFood } from "../../queries/caches";
import { findById } from "../../utils/planUtils";
import {
  convertByNetWeight,
  formatPurchaseDescription,
  selectPurchaseUnit,
} from "../../utils/purchaseUtils";
import { ProductPickerDrawer, ProductChoice } from "../ProductPickerDrawer";

export interface ConsumedProductSelection {
  purchaseId: number;
  purchaseDescription: string;
  purchaseUnitId: number;
  purchaseUnitDescription: string;
  amount: number;
}

export function ConsumedProductPicker(props: {
  intake: IntakeWithEditedFlags;
  onApply: (selection: ConsumedProductSelection) => void;
  onClose: () => void;
}) {
  const purchase = usePurchase(props.intake.purchaseUnitPurchaseId);
  const alternatives = usePurchasesByFood(purchase?.foodId);
  const createFood = useFood(purchase?.foodId);
  const choices: ProductChoice[] = alternatives.map((p) => ({
    id: p.id,
    label: formatPurchaseDescription(p),
    kcal: p.kcal,
  }));

  const handleSelect = (id: number | null) => {
    if (id == null) {
      props.onClose();
      return;
    }
    const newPurchase = findById(alternatives, id);
    if (!newPurchase || newPurchase.purchaseUnits.length === 0) {
      props.onClose();
      return;
    }
    const oldUnit = (purchase?.purchaseUnits ?? []).find(
      (pu) => pu.id === props.intake.purchaseUnitId,
    );
    const oldNW = oldUnit?.netWeight ?? 1;
    const newPU = selectPurchaseUnit(newPurchase, {
      foodUnitId: oldUnit?.foodUnitId,
      oldNetWeight: oldNW,
      oldUnitDescription: oldUnit?.description,
    });
    props.onApply({
      purchaseId: newPurchase.id,
      purchaseDescription: newPurchase.description,
      purchaseUnitId: newPU?.id ?? -1,
      purchaseUnitDescription: newPU?.description ?? "",
      amount: convertByNetWeight(props.intake.amount, oldNW, newPU?.netWeight),
    });
    props.onClose();
  };

  return (
    <ProductPickerDrawer
      open
      onClose={props.onClose}
      title={
        formatPurchaseDescription(purchase) ||
        props.intake.purchaseUnitPurchaseDescription
      }
      choices={choices}
      selectedId={props.intake.purchaseUnitPurchaseId}
      createFood={createFood}
      onSelect={handleSelect}
    />
  );
}
